import type { Country } from '@design-system/components/Footer/FooterAsideLanguage.props'
import originalCountries from '../data/countries.json'

/**
 * Represents a mapping of country codes to their corresponding values.
 * Used for creating key-value pairs of country-related information.
 *
 * @typedef {Object.<string, string>} CountryMap
 * @property {string} [key] - Country code as the key
 * @property {string} [value] - Corresponding value (e.g., default locale)
 */
type CountryMap = {
  [x: string]: string
}

/**
 * Complete list of all countries from the original countries data.
 * Imported from a JSON file containing comprehensive country information.
 *
 * @type {Country[]}
 * @constant
 * @readonly
 */
export const allCountries: Country[] = originalCountries as Country[]

/**
 * List of active countries with market codes and not closed.
 * Includes a special 'wx' country code for specific use cases.
 *
 * @type {Country[]}
 * @constant
 * @description Filters countries that are active in the market
 */
export const countries: Country[] = allCountries.filter(
  country =>
    (country.cl?.marketCode && !country.closed) || country.code === 'wx'
)

/**
 * List of countries marked as closed.
 * Represents countries that are currently inactive or unavailable.
 *
 * @type {Country[]}
 * @constant
 * @description Filters countries that are closed or not in active use
 */
export const closedCountries: Country[] = allCountries.filter(
  country => country.closed
)

/**
 * List of countries that are not closed.
 * Represents countries currently active and available.
 *
 * @type {Country[]}
 * @constant
 * @description Filters countries that are open and in active use
 */
export const openCountries: Country[] = allCountries.filter(
  country => !country.closed
)

/**
 * Generates an array of active language-country combinations.
 * Creates unique locale identifiers for active countries and their languages.
 *
 * @returns {string[]} Array of language-country combinations (e.g., 'en-US')
 * @throws {Error} If country or language data is malformed
 * @example
 * // Returns ['en-US', 'fr-CA', ...]
 * const combinations = getAllActiveLanguageCountryCombinations();
 */
export const getAllActiveLanguageCountryCombinations = (): string[] => {
  const combinations: string[] = []
  countries.forEach(country => {
    country.languages.forEach(language => {
      combinations.push(`${language.langcode}-${country.code}`)
    })
  })

  return combinations
}

/**
 * Creates a mapping of country codes to their default locale paths.
 * Provides a comprehensive lookup of default locales for each country.
 *
 * @returns {CountryMap} Object with country codes as keys and default locales as values
 * @description
 * Special handling for certain language-country combinations:
 * - For 'de', 'it', 'es', 'fr', prefers matching language-country codes
 * - Ensures each country has a single default locale
 * - Prioritizes exact language-country matches
 *
 * @throws {Error} If active language-country combinations cannot be generated
 * @example
 * // Returns { 'US': 'en-US', 'FR': 'fr-FR', ... }
 * const defaultPaths = getAllDefaultPathForEachCountry();
 */
export const getAllDefaultPathForEachCountry = (): CountryMap => {
  const localeMap: CountryMap = {}
  const activeCountries = getAllActiveLanguageCountryCombinations()
  activeCountries.forEach(locale => {
    const [lang, country] = locale.split('-')
    if (!localeMap[country]) {
      if (['de', 'it', 'es', 'fr'].includes(lang) && lang === country) {
        localeMap[country] = locale
      } else {
        localeMap[country] = locale
      }
    }
  })
  return localeMap
}

/**
 * Retrieves available href country combinations.
 * Generates an array of uppercase language-country combinations with href.
 *
 * @returns {string[]} Array of uppercase language-country combinations with href
 * @throws {Error} If country data is malformed or href information is missing
 * @example
 * // Returns ['EN-US', 'FR-CA', ...]
 * const hrefCombinations = getAvailableHrefCountryCombinations();
 */
export const getAvailableHrefCountryCombinations = (): string[] => {
  const combinations: string[] = []
  countries
    .filter(country => country.hrefCountry)
    .forEach(country => {
      country.languages.forEach(language => {
        combinations.push(`${language.langcode}-${country.code.toUpperCase()}`)
      })
    })

  return combinations
}
